<script lang="ts" setup>
import { computed, provide, toRefs } from 'vue';

import { useBookTicker, type BookTickProvider } from '@exchange/libs/book-ticker/service/src';
import useBreakpoints from '@exchange/libs/composables/shared/src/lib/useBreakpoints';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { futuresService } from '@exchange/libs/futures/service/src/index';
import { useFutures } from '@exchange/libs/futures/ui/src/lib/useFutures';
import { handleClosePosition } from '@exchange/libs/order/my-orders/service/src/lib/positions.service';

import OpenPositionsListMobile from './mobile/OpenPositionsListMobile.vue';
import OpenPositionsListDesktop from './OpenPositionsListDesktop.vue';

const props = defineProps({
  authenticatedUser: { type: Boolean, required: true },
  dragging: { type: Boolean, required: true },
  marketId: { type: String, required: true },
  showAllMarkets: { type: Boolean, required: true },
});

const { marketId, showAllMarkets } = toRefs(props);
const { getFuturesPositions: futuresPositions } = useFutures(marketId, showAllMarkets);

// Subscribe to book tickers for all positions
const activeMarkets = computed(() => Array.from(new Set(futuresPositions.value.map((position) => position.instrument_code))));
const { getBookTickForMarketId } = useBookTicker(activeMarkets);

const bookTickProvider: BookTickProvider = { getBookTickForMarketId };
provide('bookTickProvider', bookTickProvider);
const { leverage: maxLeverage } = futuresService;

const breakpoints = {
  780: 'hide-type compact-side hide-currency compact-status', // 'compact-side compact-order-type hide-time hide-currency compact-status compact-order-type',
  850: 'hide-type compact-side', // 'compact-side compact-order-type hide-time compact-order-type',
  880: 'hide-type compact-side', // 'compact-side compact-order-type compact-order-type',
  920: 'hide-type compact-side', // 'compact-side compact-order-type',
  bigger: 'hide-type',
};

/* eslint-disable compat/compat */
// @ts-ignore Wait for a proper Houdini API support
const { currentBreakPoint, watchedEl: wrapperRef } = useBreakpoints(breakpoints);

const { isXS } = useMQBreakpoints();
</script>

<template>
  <div
    ref="wrapperRef"
    class="open-positions"
  >
    <open-positions-list-desktop
      v-if="!isXS"
      class="open-positions__list"
      :show-order-type="false"
      :breakpoint="currentBreakPoint"
      :dragging="dragging"
      :market-id="marketId"
      :show-all-markets="showAllMarkets"
      :authenticated-user="authenticatedUser"
      :max-leverage="maxLeverage"
      :futures-positions="futuresPositions"
      @close-position="handleClosePosition"
    />
    <open-positions-list-mobile
      v-else
      class="open-positions__list"
      :market-id="marketId"
      :show-all-markets="showAllMarkets"
      :authenticated-user="authenticatedUser"
      :max-leverage="maxLeverage"
      :futures-positions="futuresPositions"
      @close-position="handleClosePosition"
    />
  </div>
</template>

<style lang="scss">
.open-positions {
  display: flex;
  flex-direction: column;
}
</style>
